import { useCallback, useEffect, useRef, useState } from 'react';
import { apiSaveFormDraft, apiSubmitForm } from '../utils/rmsApi';
import { withServerFormContext } from '../utils/serverFormContext';
import useRmsAnalytics from '../hooks/useRmsAnalytics';
import { useRmsState } from '../context/RmsStateContext';

/**
 * useRmsPostFormInstance
 * Orchestrates schema fetch, ensure+hydrate instance, and save/submit helpers.
 * 
 * Prefill handling:
 *   - Backend may return `prefillData` with suggested answers (e.g., school user's
 *     education setting info from previous submissions)
 *   - Prefilled answers are merged into the form state but tracked separately
 *   - Progress calculation should NOT count prefilled answers as "complete"
 *   - When user clicks Next on a prefilled question, it's saved and marked as confirmed
 */
// `enabled` holds the fetch until the caller knows who is filling the form:
// the instance id is role-suffixed, so asking before the role is resolved
// makes the server create a stray `<formKey>_unknown` draft.
// `forceFresh` (a "Try again") always asks the server again, whatever is cached.
export default function useRmsPostFormInstance({ orgId, patientRecordId, formKey, formContext, enabled = true, forceFresh = false }) {
  const [schema, setSchema] = useState(null);
  const [instanceId, setInstanceId] = useState('');
  const [answers, setAnswers] = useState({});
  const [initialStepId, setInitialStepId] = useState(null);
  const [serverSectionProgress, setServerSectionProgress] = useState(null);
  // Form instance status: 'draft' | 'submitted'
  // Used to determine if form should open in review mode (submitted forms)
  const [formStatus, setFormStatus] = useState('draft');
  // The context the server resolved for this form (initiatedBy, the caller's
  // role) — the only source for a caller who cannot read the record.
  const [serverContext, setServerContext] = useState(null);
  // Whether the server holds this respondent's answers locked (TDI-754): the
  // only source for a class teacher, who cannot read the record's lock facts.
  const [serverLocked, setServerLocked] = useState(false);
  // Track which question IDs were prefilled (not yet confirmed by user)
  // These should not count toward progress until user navigates through them
  const [prefillSources, setPrefillSources] = useState(new Set());
  // Set once this visit's submit has succeeded: the form's clean-up save on
  // leaving must not follow it (it recounted a submitted form's progress).
  const submittedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const analytics = useRmsAnalytics();
  const rmsState = useRmsState();
  // IMPORTANT: Do NOT use the full `rmsState` object as a hook dependency.
  // The provider intentionally re-creates the context value object when its internal
  // caches change (via `_version`), so depending on the whole object causes effects
  // to re-run and can flip `loading` back to true during normal autosaves.
  //
  // Instead, destructure the stable function references we need.
  const {
    getCachedFormBundle,
    ensureFormBundle,
    invalidateFormBundle,
    updateCachedFormBundleAnswers,
  } = rmsState || {};

  const setAnswer = useCallback((qid, value) => {
    setAnswers(prev => ({ ...prev, [qid]: value }));
  }, []);

  /**
   * Mark a question as confirmed (no longer just a prefill).
   * Called when user clicks Next on a prefilled question.
   */
  const confirmPrefill = useCallback((questionId) => {
    setPrefillSources(prev => {
      if (!prev.has(questionId)) return prev;
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  }, []);

  /**
   * Mark multiple questions as confirmed.
   * Useful when saving a step that includes multiple prefilled fields.
   */
  const confirmPrefills = useCallback((questionIds) => {
    if (!questionIds || questionIds.length === 0) return;
    setPrefillSources(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const qid of questionIds) {
        if (next.has(qid)) {
          next.delete(qid);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  // Derive a role-suffixed instanceId: `${formKey}_${role}`
  // role is taken from formContext.currentUser.role when available, else fallback to initiatedBy
  const computeInstanceId = useCallback(() => {
    try {
      const rawRole =
        (formContext && formContext.currentUser && formContext.currentUser.role) ||
        (formContext && formContext.initiatedBy) ||
        '';
      let normalized = String(rawRole || '').trim();
      // Ensure suffix is one of 'school_user' | 'guardian' when falling back to initiatedBy
      if (!normalized) {
        normalized = 'unknown';
      } else if (normalized === 'school') {
        normalized = 'school_user';
      }
      return `${formKey}_${normalized}`;
    } catch (_) {
      return `${formKey}_unknown`;
    }
  }, [formKey, formContext]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) return undefined;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setErrorCode(null);
        if (!orgId) throw new Error('Missing orgId');
        if (!patientRecordId) throw new Error('Missing patientRecordId');
        const resolvedInstanceId = computeInstanceId();
        /**
         * Helper to merge prefill data with existing answers.
         * Prefill only fills in empty slots - existing non-empty answers are preserved.
         * Returns { merged, appliedPrefillKeys }
         */
        const mergePrefillWithAnswers = (prefillData, existingAnswers) => {
          const merged = { ...existingAnswers };
          const appliedPrefillKeys = [];
          
          for (const [key, value] of Object.entries(prefillData || {})) {
            // Only apply prefill if the existing answer is empty/missing
            const existing = existingAnswers?.[key];
            const isEmpty = existing === undefined || existing === null || existing === '';
            
            if (isEmpty && value !== undefined && value !== null && value !== '') {
              merged[key] = value;
              appliedPrefillKeys.push(key);
            }
          }
          
          return { merged, appliedPrefillKeys };
        };
        
        // 1) Instant: use any cached bundle
        const cached = getCachedFormBundle?.(orgId, patientRecordId, formKey, resolvedInstanceId);
        if (cached) {
          if (cancelled) return;
          setSchema(cached.schema || null);
          setInstanceId(resolvedInstanceId);
          // For cached bundles, also handle prefill merging
          const cachedAnswers = cached.answers || {};
          const cachedPrefill = cached.prefillData || {};
          const { merged, appliedPrefillKeys } = mergePrefillWithAnswers(cachedPrefill, cachedAnswers);
          setAnswers(merged);
          setPrefillSources(new Set(appliedPrefillKeys));
          if (Array.isArray(cached.sectionProgress)) setServerSectionProgress(cached.sectionProgress);
          if (cached.initialStepId) setInitialStepId(cached.initialStepId);
          // Track form status for submitted form detection
          if (cached.status) setFormStatus(cached.status);
          if (cached.context) setServerContext(cached.context);
          if (cached.locked === true) setServerLocked(true);
        }
        // 2) Ensure fresh bundle (single backend call with graceful fallback inside context)
        // Use force: true if we don't have a cached bundle - this ensures we always fetch
        // fresh data after cache invalidation (e.g., after form submission)
        const needsForce = !cached || forceFresh;
        const bundle = await ensureFormBundle?.({ orgId, patientRecordId, formKey, instanceId: resolvedInstanceId, createIfMissing: true, force: needsForce });
        if (cancelled) return;
        // A bundle with no schema cannot render: say so, rather than leave the
        // page on "Preparing form" for ever (TDI-688).
        if (!(bundle && bundle.schema) && !(cached && cached.schema)) {
          throw new Error('The form could not be loaded');
        }
        if (bundle) {
          setSchema(bundle.schema || null);
          setInstanceId(resolvedInstanceId);
          
          // Handle prefill data: merge into answers but track which are prefilled
          // Prefilled answers should NOT count as "complete" until user confirms via Next
          const baseAnswers = bundle.answers || {};
          const prefillData = bundle.prefillData || {};
          
          const { merged, appliedPrefillKeys } = mergePrefillWithAnswers(prefillData, baseAnswers);
          setAnswers(merged);
          setPrefillSources(new Set(appliedPrefillKeys));
          
          if (Array.isArray(bundle.sectionProgress)) setServerSectionProgress(bundle.sectionProgress);
          if (bundle.initialStepId) setInitialStepId(bundle.initialStepId);
          // Track form status - 'submitted' forms should open in review mode
          if (bundle.status) setFormStatus(bundle.status);
          setServerContext(bundle.context || null);
          setServerLocked(bundle.locked === true);
        }
        // Persist instance pointer for resume
        try {
          const key = `rms:formInstance:${orgId}:${patientRecordId}:${formKey}:${resolvedInstanceId}`;
          localStorage.setItem(key, JSON.stringify({ instanceId: resolvedInstanceId, updatedAt: Date.now() }));
        } catch (_) {}
        try {
          analytics.postFormStarted({ formKey, patientRecordId });
        } catch (_) {}
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to start form');
          setErrorCode(e?.code || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, orgId, patientRecordId, formKey, analytics, computeInstanceId, getCachedFormBundle, ensureFormBundle, forceFresh]);

  /**
   * onSaveStep - Save a step's answers and current progress to Firestore
   * 
   * @param step - The question step being saved
   * @param patch - The answer patch to save
   * @param sectionProgress - Optional array of section progress percentages.
   *   When provided (on forward navigation), this is passed to the backend for storage.
   *   This ensures frontend display and Firestore stay in sync (single source of truth).
   *   Progress only updates when user clicks Next, not while typing/selecting.
   */
  const onSaveStep = useCallback(async (step, patch, sectionProgress) => {
    if (!instanceId || submittedRef.current) return;
    await apiSaveFormDraft({
      orgId,
      patientRecordId,
      formKey,
      instanceId,
      patch,
      stepId: step?.questionId,
      // A caller who cannot read the record (a class teacher) has no
      // initiatedBy of its own: send the server's, so every save carries the
      // same context the page shows (TDI-678).
      context: withServerFormContext(formContext, serverContext),
      sectionProgress // Pass to backend for persistence (single source of truth)
    });
    // Keep in-memory bundle cache in sync with the user's latest edits.
    // This prevents a confusing UX where navigating away/back immediately re-hydrates
    // from a stale cached bundle (often containing older auto-complete answers).
    try {
      updateCachedFormBundleAnswers?.({
        orgId,
        patientRecordId,
        formKey,
        instanceId,
        patch,
        sectionProgress,
        initialStepId: step?.questionId,
      });
    } catch (_) {}
  }, [instanceId, orgId, patientRecordId, formKey, formContext, serverContext, updateCachedFormBundleAnswers]);

  const onComplete = useCallback(async (finalAnswers) => {
    if (!instanceId) return;
    try {
      await apiSaveFormDraft({
        orgId,
        patientRecordId,
        formKey,
        instanceId,
        patch: finalAnswers
      });
    } catch (_e) {}
    
    await apiSubmitForm({
      orgId,
      patientRecordId,
      formKey,
      instanceId,
      idempotencyKey: `${instanceId}-submit`
    });
    submittedRef.current = true;
    
    // After successful submission, INVALIDATE the form bundle cache entirely.
    // This is critical because:
    //   1. The form's status has changed from 'draft' to 'submitted'
    //   2. The backend may have processed participant updates (e.g., guardian nomination changes)
    //   3. Prefill data may have been updated based on the submitted answers
    // 
    // Previously we used updateCachedFormBundleAnswers, but this only patched answers
    // and didn't update status or other metadata. Invalidating forces a fresh fetch
    // from Firestore on next page visit, ensuring the user sees accurate data.
    // 
    // This fixes the bug where: "Update parent name → Edit → Submit → Back to form → 
    // Name reverted to original" because the stale cache was being served.
    try {
      invalidateFormBundle?.({
        orgId,
        patientRecordId,
        formKey,
        instanceId,
      });
    } catch (_) {}
    
    try {
      analytics.postFormSubmitted({ formKey, patientRecordId });
    } catch (_) {}
  }, [instanceId, orgId, patientRecordId, formKey, analytics, invalidateFormBundle]);

  return {
    schema,
    instanceId,
    answers,
    setAnswer,
    initialStepId,
    serverSectionProgress,
    // Form status: 'draft' | 'submitted'
    // Submitted forms should open in review mode to show user their completed answers
    formStatus,
    serverContext,
    serverLocked,
    // Prefill tracking: questions that were auto-filled from previous submissions
    // These should NOT count as "answered" for progress until confirmed
    prefillSources,
    confirmPrefill,
    confirmPrefills,
    loading,
    error,
    // The callable's error code ('functions/permission-denied' and so on), so
    // the page can tell a refusal from a load that failed.
    errorCode,
    onSaveStep,
    onComplete
  };
}


